// A simple seeded pseudo-random number generator (PRNG)
class SeededRandom {
  private seed: number;

  constructor(seed: string | number) {
    // Convert string seed to a number. A simple hash or checksum can work.
    // For simplicity, let's use a basic string hash if it's a string.
    this.seed = typeof seed === 'string' ? this.hashCode(seed) : seed;
  }

  private hashCode(s: string): number {
    let hash = 0;
    for (let i = 0; i < s.length; i++) {
      const char = s.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }

  // LCG parameters (constants often used)
  private m = 0x80000000; // 2**31
  private a = 1103515245;
  private c = 12345;

  next(): number {
    this.seed = (this.a * this.seed + this.c) % this.m;
    return this.seed / (this.m - 1);
  }
}

export function seededShuffleArray<T extends { promote?: boolean }>(
  array: T[],
  seed: string | number,
  promote: boolean = false
): T[] {
  if (!promote) {
    const shuffledArray = [...array]; // Create a shallow copy
    const rng = new SeededRandom(seed);
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(rng.next() * (i + 1));
      [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
    }
    return shuffledArray;
  }

  // Separate items into promoted and not-promoted groups
  const promotedItems: T[] = [];
  const notPromotedItems: T[] = [];
  for (const item of array) {
    if (item.promote) {
      promotedItems.push(item);
    } else {
      notPromotedItems.push(item);
    }
  }

  // Shuffle each group independently but deterministically
  const shuffledPromoted = seededShuffleArray(promotedItems, seed, false);
  const shuffledNotPromoted = seededShuffleArray(notPromotedItems, seed, false);

  // Combine them, with promoted items first
  return [...shuffledPromoted, ...shuffledNotPromoted].filter(item => item !== undefined);
}

export const feedSelectFn = (shuffleSeed: string|number) => (data: any) => {
  if (data && data.news && Array.isArray(data.news.items)) {
    return {
      ...data,
      news: {
        ...data.news,
        // Always enable promotion logic for feeds
        items: seededShuffleArray(data.news.items, shuffleSeed, true),
      },
    };
  }
  return data;
};
